/**
 * BusinessWorkspace — Persistent home for a business.
 *
 * The durable center of the V2 deal flow. Tabs:
 *   - Overview: business summary + quick actions
 *   - Creatives: creative library (Phase 2)
 *   - Proposals: proposal history (Phase 4)
 *   - Campaigns: active campaigns (Phase 5)
 *   - Activity: timeline (Phase 5)
 *
 * Replaces the linear wizard hub — sellers always return here between actions.
 */
import React, { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  LayoutGrid, Video, FileText, BarChart3, Clock, RefreshCw, CircleCheckBig, TriangleAlert,
} from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import BusinessWorkspaceHeader from '@/components/deal/BusinessWorkspaceHeader';
import BusinessOverviewTab from '@/components/deal/BusinessOverviewTab';
import CreativeLibraryTab from '@/components/deal/CreativeLibraryTab';
import ProposalsTab from '@/components/deal/ProposalsTab';
import ActivityTab from '@/components/deal/ActivityTab';
import CampaignsTab from '@/components/deal/CampaignsTab';
import useDealFlow from '@/hooks/useDealFlow';
import {
  generateDealId,
  getLatestProposalForBusiness,
} from '@/components/deal/mockDealData';
import { toast } from 'sonner';
import { SellerFlowApiError } from '@/services/sellerFlowApi';

const PITCH_BINDING_STORAGE_PREFIX = 'cornercast_pitchkit_binding';

function clearRememberedPitchBindingForBusiness(businessId, bindingId) {
  if (!businessId || !bindingId) return;
  try {
    const storageKey = `${PITCH_BINDING_STORAGE_PREFIX}:${businessId}`;
    if (sessionStorage.getItem(storageKey) === bindingId) {
      sessionStorage.removeItem(storageKey);
    }
  } catch {
    // ignore storage failures
  }
}

export default function BusinessWorkspace() {
  const { orgSlug, businessId } = useParams();
  const navigate = useNavigate();
  const deal = useDealFlow();

  // Ensure active business is set
  useEffect(() => {
    if (businessId && deal.setActiveBusinessId) {
      deal.setActiveBusinessId(businessId);
    }
  }, [businessId, deal.setActiveBusinessId]);

  useEffect(() => {
    if (!businessId) return;
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [businessId]);

  useEffect(() => {
    if (!businessId) return;
    deal.loadWorkspaceBundle(businessId, { setActive: true }).catch((error) => {
      console.error('Failed to load workspace', error);
      toast.error('Could not load this business workspace');
    });
  }, [businessId, deal.loadWorkspaceBundle]);

  useEffect(() => {
    if (!businessId) return;
    deal.refreshBusinessProfileForBusiness(businessId, { setActive: true }).catch((error) => {
      console.warn('Could not refresh business-profile state', error);
    });
  }, [businessId, deal.refreshBusinessProfileForBusiness]);

  // Get business data — either from the active business record or fall back to businessProfile
  const business = deal.businesses?.[businessId] || null;
  const bp = deal.businessProfilesByBusiness?.[businessId]
    || (deal.activeBusinessId === businessId ? deal.businessProfile : null)
    || deal.businessProfile;
  const enrichmentStatus = bp?.enrichmentStatus || null;
  const enrichmentErrorDetail = bp?.enrichmentErrorDetail || null;
  const creativeGenerationDisabled = enrichmentStatus && enrichmentStatus !== 'ready';
  const creativeGenerationMessage = enrichmentStatus === 'pending'
    ? 'We found the business and are building the profile now. Creative generation unlocks as soon as enrichment finishes.'
    : enrichmentStatus === 'failed'
      ? enrichmentErrorDetail || 'The business profile build failed. Retry it to unlock creative generation.'
      : '';

  useEffect(() => {
    if (!businessId || enrichmentStatus !== 'pending') return undefined;

    let cancelled = false;
    let timerId = null;

    const poll = async () => {
      try {
        await deal.refreshBusinessProfileForBusiness(businessId, { setActive: true });
      } catch (error) {
        if (!cancelled) {
          console.warn('Could not refresh pending business-profile state', error);
        }
      } finally {
        if (!cancelled) {
          timerId = window.setTimeout(poll, 5000);
        }
      }
    };

    timerId = window.setTimeout(poll, 5000);

    return () => {
      cancelled = true;
      if (timerId) {
        window.clearTimeout(timerId);
      }
    };
  }, [businessId, deal.refreshBusinessProfileForBusiness, enrichmentStatus]);

  // Derived data
  const businessName = business?.name || bp?.name || 'Business';
  const category = business?.category || bp?.category || '';
  const scope = business?.scope || deal.scope?.type || null;
  const creativesCount = deal.creatives?.[businessId]?.length || 0;
  const proposalsCount = deal.proposals?.[businessId]?.length || 0;
  const campaigns = deal.campaignsByBusiness?.[businessId] || [];
  const activities = deal.workspaceActivityByBusiness?.[businessId] || [];
  const blockerSummary = deal.launchReadinessByBusiness?.[businessId] || {
    allReady: false,
    blockers: [],
    readyItems: [],
    items: [],
    progressPct: 0,
  };
  const workspaceTabs = [
    { value: 'overview', label: 'Overview', icon: LayoutGrid },
    { value: 'creatives', label: 'Creatives', icon: Video, count: creativesCount || null },
    { value: 'proposals', label: 'Proposals', icon: FileText, count: proposalsCount || null },
    { value: 'campaigns', label: 'Campaigns', icon: BarChart3, count: campaigns.length || null },
    { value: 'activity', label: 'Activity', icon: Clock },
  ];

  const applyProposalStatus = async (currentProposal, status) => {
    await deal.recordProposalResponseForBusiness(businessId, currentProposal.id, status);
  };

  // ── Navigation handlers ─────────────────────────────────────────────

  const openPitch = ({ fresh = false, bindingId = null } = {}) => {
    const id = deal.dealId || generateDealId();
    if (!deal.dealId) deal.setDealId(id);
    // Reset downstream state so flow starts fresh
    deal.setProposal(null);
    deal.setAgreement(null);
    const params = new URLSearchParams();
    if (fresh) {
      params.set('fresh', '1');
    } else if (bindingId) {
      params.set('binding_id', bindingId);
    }
    const query = params.toString();
    navigate(`/org/${orgSlug}/deal/${id}/pitch${query ? `?${query}` : ''}`);
  };

  const handleGenerateCreative = () => {
    if (creativeGenerationDisabled) return;
    const recoverableCreative = (deal.creatives?.[businessId] || []).find((creative) =>
      ['processing', 'failed', 'rejected'].includes(creative.status),
    );
    openPitch({ bindingId: recoverableCreative?.bindingId || null });
  };

  const handleGenerateNewCreative = () => {
    if (creativeGenerationDisabled) return;
    openPitch({ fresh: true });
  };

  const handleBuildOffer = () => {
    const id = deal.dealId || generateDealId();
    if (!deal.dealId) deal.setDealId(id);
    // Reset downstream state so flow starts fresh
    deal.setProposal(null);
    deal.setAgreement(null);
    navigate(`/org/${orgSlug}/deal/${id}/offer`);
  };

  const handleViewProposals = () => {
    const id = deal.dealId || generateDealId();
    if (!deal.dealId) deal.setDealId(id);
    navigate(`/org/${orgSlug}/deal/${id}/proposal`);
  };

  const handleBack = () => {
    navigate(deal.returnTo || `/org/${orgSlug}/leads`);
  };

  const handleRetryBusinessProfileEnrichment = async () => {
    try {
      await deal.enrichBusinessProfileForBusiness(businessId, null, { setActive: true });
      toast.success('Business profile build restarted');
    } catch (error) {
      console.error('Failed to retry business profile enrichment', error);
      toast.error(error?.message || 'Could not retry the business profile build');
    }
  };

  const handleRenameCreative = async (creative, creativeName) => {
    try {
      await deal.renameCreativeForBusiness(businessId, creative, creativeName);
      toast.success('Creative name updated');
    } catch (error) {
      console.error('Failed to rename creative', error);
      toast.error(error?.message || 'Could not rename this creative');
      throw error;
    }
  };

  const handleDeleteCreativeDraft = async (creative) => {
    try {
      await deal.deleteCreativeForBusiness(businessId, creative);
      clearRememberedPitchBindingForBusiness(businessId, creative?.bindingId || creative?.id || null);
      toast.success('Draft creative deleted');
    } catch (error) {
      console.error('Failed to delete creative draft', error);
      if (error instanceof SellerFlowApiError && error.isCreativeDeleteConflict) {
        toast.error(error.creativeDeleteConflictMessage);
      } else {
        toast.error(error?.message || 'Could not delete this creative');
      }
      throw error;
    }
  };

  return (
    <>
      <BusinessWorkspaceHeader
        businessName={businessName}
        category={category}
        scope={scope}
        address={business?.address || bp?.address}
        website={business?.website_url || bp?.website_url}
        creativesCount={creativesCount}
        proposalsCount={proposalsCount}
        campaignCount={campaigns.length}
        onBack={handleBack}
      />

      <div className="mx-auto max-w-6xl px-4 py-6 sm:px-6">
        {enrichmentStatus ? (
          <Card className={`mb-6 gap-0 shadow-sm ${
            enrichmentStatus === 'ready'
              ? 'border-emerald-200 bg-emerald-50/60'
              : enrichmentStatus === 'failed'
                ? 'border-amber-200 bg-amber-50/70'
                : 'border-blue-200 bg-blue-50/70'
          }`}
          >
            <CardContent className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:justify-between">
              <div className="flex items-start gap-3">
                <div className={`mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl ${
                  enrichmentStatus === 'ready'
                    ? 'bg-emerald-100 text-emerald-700'
                    : enrichmentStatus === 'failed'
                      ? 'bg-amber-100 text-amber-700'
                      : 'bg-blue-100 text-blue-700'
                }`}
                >
                  {enrichmentStatus === 'ready' ? (
                    <CircleCheckBig className="h-5 w-5" />
                  ) : enrichmentStatus === 'failed' ? (
                    <TriangleAlert className="h-5 w-5" />
                  ) : (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  )}
                </div>
                <div>
                  <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-slate-500">
                    Business profile
                  </p>
                  <h2 className="mt-1 text-base font-semibold text-slate-950">
                    {enrichmentStatus === 'ready'
                      ? 'Ready for creative generation'
                      : enrichmentStatus === 'failed'
                        ? 'Profile build needs attention'
                        : 'Building the business profile'}
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-slate-600">
                    {enrichmentStatus === 'ready'
                      ? 'The business profile is ready. Create your first video now.'
                      : creativeGenerationMessage}
                  </p>
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                {enrichmentStatus === 'pending' ? (
                  <Button variant="outline" onClick={() => deal.refreshBusinessProfileForBusiness(businessId, { setActive: true })} className="rounded-xl bg-white">
                    Refresh status
                  </Button>
                ) : null}
                {enrichmentStatus === 'failed' ? (
                  <Button onClick={handleRetryBusinessProfileEnrichment} className="rounded-xl bg-coral text-white hover:bg-coral/90">
                    Retry profile build
                  </Button>
                ) : null}
              </div>
            </CardContent>
          </Card>
        ) : null}

        <Tabs defaultValue="overview">
          <div className="mb-6 border-b border-slate-200">
            <TabsList
              variant="line"
              className="h-auto min-w-full justify-start gap-1 overflow-x-auto rounded-none border-0 bg-transparent pb-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            >
              {workspaceTabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    className="h-11 flex-none gap-2 rounded-none border-0 px-3 py-0 text-sm font-medium text-slate-500 hover:text-slate-700 data-[state=active]:text-slate-950 after:left-3 after:right-3 after:bottom-0 after:h-0.5 after:bg-slate-950 [&_svg]:text-slate-400 data-[state=active]:[&_svg]:text-slate-700 [&_[data-count]]:bg-slate-100 [&_[data-count]]:text-slate-500 data-[state=active]:[&_[data-count]]:bg-slate-950 data-[state=active]:[&_[data-count]]:text-white"
                  >
                    <Icon className="h-4 w-4" />
                    <span>{tab.label}</span>
                    {tab.count ? (
                      <span
                        data-count
                        className="inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-medium transition-colors"
                      >
                        {tab.count}
                      </span>
                    ) : null}
                  </TabsTrigger>
                );
              })}
            </TabsList>
          </div>

          {/* Overview */}
          <TabsContent value="overview">
            <BusinessOverviewTab
              businessProfile={bp}
              businessEnrichment={bp}
              business={business}
              scope={scope}
              lead={deal.lead}
              onGenerateCreative={handleGenerateCreative}
              onBuildOffer={handleBuildOffer}
              onViewProposals={handleViewProposals}
              onRetryBusinessProfileEnrichment={handleRetryBusinessProfileEnrichment}
              creativesCount={creativesCount}
              proposalsCount={proposalsCount}
              campaignCount={campaigns.length}
              activities={activities}
              blockerSummary={blockerSummary}
              latestCreative={(deal.creatives?.[businessId] || []).filter((c) => c.status !== 'archived').at(-1) || null}
              latestProposal={getLatestProposalForBusiness(deal.proposals?.[businessId] || [])}
            />
          </TabsContent>

          {/* Creatives — Phase 2 */}
          <TabsContent value="creatives">
            <CreativeLibraryTab
              creatives={deal.creatives?.[businessId] || []}
              proposals={deal.proposals?.[businessId] || []}
              onGenerateNew={handleGenerateNewCreative}
              onGenerateAlternative={handleGenerateNewCreative}
              onRenameCreative={handleRenameCreative}
              onDeleteDraft={handleDeleteCreativeDraft}
              generateDisabled={creativeGenerationDisabled}
              generateDisabledReason={creativeGenerationMessage}
              onRetryGenerationUnlock={enrichmentStatus === 'failed' ? handleRetryBusinessProfileEnrichment : null}
              onUseInProposal={async (creative) => {
                try {
                  await deal.selectCreativeForBusiness(businessId, creative);
                  const id = deal.dealId || generateDealId();
                  if (!deal.dealId) {
                    deal.setDealId(id);
                  }
                  navigate(`/org/${orgSlug}/deal/${id}/offer`);
                } catch (error) {
                  console.error('Failed to select creative', error);
                  toast.error('Could not attach that creative to the workspace');
                }
              }}
              onEdit={(creative) => {
                const id = deal.dealId || generateDealId();
                if (!deal.dealId) {
                  deal.setDealId(id);
                }
                const bindingId = creative?.bindingId || creative?.id || null;
                openPitch({ bindingId });
              }}
              onDuplicate={() => {
                toast.info('Creative duplication is not wired to the backend yet.');
              }}
              onUpdateStatus={(creative, status) => {
                deal.updateCreativeStatus(businessId, creative.id, status);
              }}
            />
          </TabsContent>

          {/* Proposals — Phase 4 + Phase 13 lifecycle */}
          <TabsContent value="proposals">
            <ProposalsTab
              proposals={deal.proposals?.[businessId] || []}
              creatives={deal.creatives?.[businessId] || []}
              onNewProposal={handleBuildOffer}
              onViewProposal={(proposal) => {
                // Load proposal into deal state for read-only viewing
                deal.setProposal(proposal);
                const id = deal.dealId || generateDealId();
                if (!deal.dealId) deal.setDealId(id);
                navigate(`/org/${orgSlug}/deal/${id}/proposal`);
              }}
              onEditProposal={(proposal) => {
                // Load proposal into deal state for editing (draft)
                deal.setProposal(proposal);
                const id = deal.dealId || generateDealId();
                if (!deal.dealId) deal.setDealId(id);
                navigate(`/org/${orgSlug}/deal/${id}/proposal`);
              }}
              onFollowUp={(proposal) => {
                toast.success(`Follow-up reminder set for ${proposal.packageName || 'proposal'}`);
              }}
              onRevise={async (proposal) => {
                try {
                  const revised = await deal.createProposalRevisionForBusiness(businessId, proposal.id);
                  deal.setProposal(revised);
                  const id = deal.dealId || generateDealId();
                  if (!deal.dealId) deal.setDealId(id);
                  navigate(`/org/${orgSlug}/deal/${id}/proposal`);
                } catch (error) {
                  console.error('Failed to create proposal revision', error);
                  toast.error('Could not create a revision for this proposal');
                }
              }}
              onCloneAsNew={async (proposal) => {
                try {
                  const revised = await deal.createProposalRevisionForBusiness(businessId, proposal.id);
                  deal.setProposal(revised);
                  const id = deal.dealId || generateDealId();
                  if (!deal.dealId) deal.setDealId(id);
                  navigate(`/org/${orgSlug}/deal/${id}/proposal`);
                  toast.success('Proposal cloned as new draft');
                } catch (error) {
                  console.error('Failed to clone proposal', error);
                  toast.error('Could not clone this proposal');
                }
              }}
              onUpdateStatus={async (proposal, status) => {
                try {
                  if (status === 'sent') {
                    await deal.sendProposalForBusiness(businessId, proposal);
                  } else {
                    await applyProposalStatus(proposal, status);
                  }
                  toast.success(`Proposal marked as ${status}`);
                } catch (error) {
                  console.error('Failed to update proposal status', error);
                  toast.error('Could not update proposal status');
                }
              }}
            />
          </TabsContent>

          {/* Campaigns — Phase 5 */}
          <TabsContent value="campaigns">
            <CampaignsTab
              campaigns={campaigns}
              onViewCampaign={(campaign) => {
                if (deal.dealId) {
                  navigate(`/org/${orgSlug}/deal/${deal.dealId}/live`);
                }
              }}
            />
          </TabsContent>

          {/* Activity — Phase 5 */}
          <TabsContent value="activity">
            <ActivityTab activities={activities} />
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
}
